import { Download as DownloadIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Stack,
  Typography
} from '@mui/material'
import { t } from 'i18next'
import { useState } from 'react'

import { formatDateExtendedIT } from '../../../helpers/date-formatter.js'
import BarChart from '../../Charts/BarChart.jsx'
import ChartReferenceValues from '../../Charts/ChartReferenceValues'
import LineChart from '../../Charts/LineChart.jsx'
import LineSteppedChart from '../../Charts/LineSteppedChart.jsx'
import Show from '../../Layout/Can/Show'
import { useGlobalContext } from '../../Shared/Contexts/GlobalContext.jsx'
import { useDevices } from '../Contexts/DevicesContext'

const MeasuresTab = ({ chart = [], quantities = [], device = {} }) => {
  const { theme, openErrorAlert, dateRange } = useGlobalContext()
  const { useDeviceMeasurementsDownload } = useDevices()

  const visibleQuantities = quantities.filter((q) => !q.hidden)

  const getLastMeasure = (data) => {
    let orderedData = data.slice()
    orderedData.sort((a, b) => a.x - b.x)

    return orderedData[orderedData.length - 1]
  }

  const [downloadInProgress, setDownloadInProgress] = useState(false)

  const handleDownloadMeasures = () => {
    setDownloadInProgress(true)
    useDeviceMeasurementsDownload({
      id: device.id,
      name: device.name,
      serial: device.serial,
      from: dateRange.from,
      to: dateRange.to
    })
      .catch(() => openErrorAlert({ keys: ['download'] }))
      .finally(() => setDownloadInProgress(false))
  }

  return (
    <>
      <Stack
        direction="column"
        spacing={5}>
        {visibleQuantities?.map(
          (q, i) =>
            chart[q.measureName]?.length > 0 && (
              <Box key={i}>
                <Box
                  key={q.measureName}
                  id={q.measureName}>
                  <Card variant="standard">
                    <CardHeader
                      title={t(q.name)}
                      subheader={
                        <Typography variant="h6">
                          {formatDateExtendedIT(getLastMeasure(chart[q.measureName]).x)} - {q.chart === 'stepped' ? q.labels[getLastMeasure(chart[q.measureName]).y] : `${getLastMeasure(chart[q.measureName]).y}${q.unitOfMeasure}`}
                        </Typography>}
                    />
                    <CardContent>
                      {q.chart === 'stepped' && (
                        <LineSteppedChart
                          data={chart[q.measureName]}
                          unitOfMeasure={q.unitOfMeasure}
                          measureName={q.measureName}
                          labels={q.labels}
                        />
                      )}

                      {q.chart === 'line' && (
                        <LineChart
                          data={chart[q.measureName]}
                          unitOfMeasure={q.unitOfMeasure}
                          measureName={q.measureName}
                          theme={theme}
                          curve={'linear'}
                        />
                      )}

                      {q.chart === 'bar' && (
                        <BarChart
                          data={chart[q.measureName]}
                          unitOfMeasure={q.unitOfMeasure}
                          measureName={q.measureName}
                        />
                      )}
                    </CardContent>

                    <Show when={() => q.summary}>
                      <CardActions>
                        <ChartReferenceValues
                          data={chart[q.measureName]}
                          unitOfMeasure={q.unitOfMeasure}></ChartReferenceValues>
                      </CardActions>
                    </Show>
                  </Card>
                </Box>
              </Box>
            )
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        align="center"
        marginTop={3}>
        <Show when={() => downloadInProgress}>
          <CircularProgress size={30} />
        </Show>
        <Show when={() => !downloadInProgress}>
          <Button
            id="download-measures-xlsx"
            variant="contained"
            startIcon={<DownloadIcon fontSize="small" />}
            onClick={handleDownloadMeasures}>
            {t('common.download')} XLSX
          </Button>
        </Show>
      </Stack>
    </>
  )
}

export default MeasuresTab
